import { CookieBannerActions, CookieBannerEvents } from './actions';
import { CookieBannerState, CookieCategories, CookieServices, isCookieCategories, isCookieServices } from './types';

export const initialState: CookieBannerState = {
    consented: false,
    initialized: false,
    ready: false,
};

function hasConsented(categories: CookieBannerState['categories']): boolean {
    if (!categories) {
        return false;
    }

    return Object.values(categories).some((value) => value === true);
}

function isReady(state: CookieBannerState): boolean {
    const hasInitialized = state.initialized === true;
    const hasScanData = Boolean(state.cookieScan?.cookies?.length);

    if (!hasInitialized || !hasScanData) {
        return false;
    }

    /**
     * If the user has already consented, has the preferences data been
     * loaded from localStorage?
     */
    if (state.consented) {
        const hasCategories = Boolean(state.categories && Object.keys(state.categories).length > 0);

        return hasCategories;
    }

    return true;
}

function getCategoriesFromPayload(payload?: CookieCategories): CookieCategories | undefined {
    if (!payload || typeof payload !== 'object') {
        return undefined;
    }

    return {
        advertising: payload.advertising,
        analytics: payload.analytics,
        functional: payload.functional,
        marketing: payload.marketing,
        necessary: payload.necessary,
        performance: payload.performance,
    };
}

function getServicesFromPayload(payload?: CookieServices): CookieServices | undefined {
    if (!payload || typeof payload !== 'object') {
        return undefined;
    }

    return {
        ...payload,
    };
}

export function cookieBannerReducer(state = initialState, action: CookieBannerActions): CookieBannerState {
    if (!action) {
        return state;
    }

    const newState = {
        ...state,
    };

    newState.ready = isReady(newState);

    switch (action.type) {
        case CookieBannerEvents.INITIALIZED: {
            newState.initialized = true;
            newState.ready = isReady(newState);

            return newState;
        }

        case CookieBannerEvents.LOADED: {
            if (isCookieCategories(action.payload)) {
                newState.categories = getCategoriesFromPayload(action.payload);
                newState.consented = hasConsented(action.payload);
                newState.ready = isReady(newState);

                return newState;
            }

            break;
        }

        case CookieBannerEvents.LOADED_SERVICES: {
            if (isCookieServices(action.payload)) {
                newState.services = getServicesFromPayload(action.payload);
                newState.ready = isReady(newState);

                return newState;
            }

            break;
        }

        case CookieBannerEvents.READY: {
            return newState;
        }

        case CookieBannerEvents.CONSENT: {
            if (isCookieCategories(action.payload)) {
                newState.categories = getCategoriesFromPayload(action.payload);
                newState.consented = hasConsented(action.payload);
                newState.ready = isReady(newState);

                return newState;
            }

            break;
        }

        case CookieBannerEvents.CONSENT_SERVICES: {
            if (isCookieServices(action.payload)) {
                newState.services = getServicesFromPayload(action.payload);
                newState.ready = isReady(newState);

                return newState;
            }

            break;
        }

        case CookieBannerEvents.CUSTOM_SERVICES_DETECTED: {
            const servicesUpdate = action.payload?.reduce((acc, service) => {
                if (!service.name) {
                    return acc;
                }

                acc[service.name] = service.enabled;

                return acc;
            }, {} as CookieServices);

            return {
                ...state,
                services: {
                    ...state.services,
                    ...servicesUpdate,
                },
            };
        }

        case CookieBannerEvents.LATEST_SCAN: {
            newState.cookieScan = action.payload;
            newState.ready = isReady(newState);

            return newState;
        }

        default:
            return state;
    }

    return state;
}
